import {AlertCircleIcon, AlertTriangleIcon, CalendarIcon, CompassIcon, DownloadIcon, Loader2Icon} from "lucide-react";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSWRConfig} from "swr";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent} from "@components/ui/card";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {Input} from "@components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/ui/select";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@components/ui/tabs";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {useIntegrations} from "@hooks/use-integrations";
import {createScenario, deleteScenario, type LedgerFilters, patchScenario, type VNextOp, useVNextOps, useVNextSyncStatus, useVNextWorkspace, VNextTab} from "@hooks/use-vnext";

import {AccountMappingTab} from "./AccountMappingTab";
import {ARTab} from "./ARTab";
import AuditTab from "./AuditTab";
import {BalanceSheetSummaryTab} from "./BalanceSheetSummaryTab";
import {BalanceSheetTab} from "./BalanceSheetTab";
import {BookingsSummaryTab} from "./BookingsSummaryTab";
import {CashFlowExplorerTab} from "./CashFlowExplorerTab";
import {CashFlowSummaryTab} from "./CashFlowSummaryTab";
import {CEOSummaryV2Tab} from "./CEOSummaryV2Tab";
import {ChecksStatusStrip} from "./ChecksStatusStrip";
import {ContactMappingTab} from "./ContactMappingTab";
import {DashboardTour} from "./DashboardTour";
import {DataSyncTab} from "./DataSyncTab";
import {DriversAssumptionsTab} from "./DriversAssumptionsTab";
import {FinancialModelTour} from "./FinancialModelTour";
import {ForecastSummaryTab} from "./ForecastSummaryTab";
// import {HealthCheckTab} from "./HealthCheckTab";
import {InsightsTab} from "./InsightsTab";
import {InstructionsTab} from "./InstructionsTab";
import {KnowledgeTab} from "./KnowledgeTab";
import {ModelingWorkbenchTab} from "./ModelingWorkbenchTab";
import {OpsTab} from "./OpsTab";
import {PlaceholderTab} from "./PlaceholderTab";
import {PnLExplorerTab} from "./PnLExplorerTab";
import {PricingTab} from "./PricingTab";
import {RevenueTab} from "./RevenueTab";
import {SaaSSummaryTab} from "./SaaSSummaryTab";
import {SandboxTab} from "./SandboxTab";
import {ScenarioSelector} from "./ScenarioSelector";
import {SummaryTab} from "./SummaryTab";
import {TransactionsTab} from "./TransactionsTab";
// import {VarianceTab} from "./VarianceTab"; // hidden for now
import {Week13ForecastPanel} from "./Week13ForecastPanel";
// import {WhatIfSidebar} from "./WhatIfSidebar"; // removed: What-If panel not currently exposed
import {XeroPnLTab} from "./XeroPnLTab";

interface TabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    compare?: string;
    initialFilters?: LedgerFilters;
    fyStartMonth?: number;
    fyEndMonth?: number;
    currentMonth?: string;
}

type PeriodPreset = "MTD" | "QTD" | "YTD" | "FY" | "MONTH" | "CUSTOM";

function buildMonthOptions(): {value: string; label: string}[] {
    const options: {value: string; label: string}[] = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const label = d.toLocaleString("en-US", {month: "short", year: "numeric"});
        options.push({value: `${yyyy}-${mm}`, label});
    }
    return options;
}

function formatMonthKey(monthKey?: string | null): string {
    if (!monthKey) return "the forecast window";
    const [year, month] = monthKey.split("-");
    const monthIndex = Number(month) - 1;
    if (!year || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return monthKey;
    return new Date(Number(year), monthIndex, 1).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
    });
}

function formatPercent(value: unknown): string | null {
    const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : null;
    if (numeric == null || !Number.isFinite(numeric)) return null;
    return `${numeric >= 0 ? "+" : ""}${numeric.toFixed(1)}%`;
}

function formatCurrency(value: unknown): string | null {
    const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : null;
    if (numeric == null || !Number.isFinite(numeric)) return null;
    return `${numeric < 0 ? "-" : ""}$${Math.abs(numeric).toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

function humanizeTarget(value?: string | null): string {
    if (!value) return "the model";
    return value.replace(/[._]+/g, " ").trim().toLowerCase();
}

function baselineMethodSummary(method: string): string {
    if (method === "avg_3m") return "Baseline uses a trailing 3-month average and excludes lines with no activity in the last 3 actual months.";
    if (method === "avg_6m") return "Baseline uses a trailing 6-month average and excludes lines with no activity in the last 3 actual months.";
    if (method === "avg_12m") return "Baseline uses a trailing 12-month average on calendar months and excludes stale lines with no activity in the last 3 actual months.";
    if (method === "last_month") return "Baseline carries the most recent actual run-rate for active lines only.";
    if (method === "yoy_flat") return "Baseline anchors to the prior-year month for active lines and holds that level flat forward.";
    if (method === "yoy_growth") return "Baseline anchors to the prior-year month for active lines and applies the stored year-on-year growth logic.";
    return `Baseline method is ${method.replace(/_/g, " ")}.`;
}

function describeScenarioOp(op: VNextOp): string {
    const target = humanizeTarget(op.target_node_label || op.target_category || op.label);
    const from = formatMonthKey(op.target_month);
    if (op.op_type === "set_override") {
        const value = formatCurrency(op.params?.value);
        return value ? `Directly overrode ${target} to ${value} in ${from}.` : `Applied a direct override to ${target} in ${from}.`;
    }
    if (op.op_type === "set_baseline") {
        return `Changed the baseline behavior for ${target} from ${from}.`;
    }
    if (op.op_type === "set_driver") {
        const driverType = String(op.params?.driver_type ?? "").toLowerCase();
        const percent = formatPercent(op.params?.value);
        const amount = formatCurrency(op.params?.value);
        if (driverType === "scale") {
            return percent ? `Scaled ${target} by ${percent} from ${from} onward.` : `Scaled ${target} from ${from} onward.`;
        }
        if (driverType === "growth") {
            return percent ? `Applied ${percent} growth to ${target} from ${from} onward.` : `Applied growth to ${target} from ${from} onward.`;
        }
        if (driverType === "fixed") {
            return amount ? `Set ${target} to ${amount} from ${from} onward.` : `Set a fixed run-rate for ${target} from ${from} onward.`;
        }
        if (driverType === "flat") {
            return `Held ${target} flat from ${from} onward.`;
        }
    }
    return op.label?.trim() ? `${op.label.trim()} (${from}).` : `Applied ${op.op_type.replace(/_/g, " ")} to ${target} from ${from}.`;
}

function buildScenarioCommentary(
    scenarioName: string,
    baselineMethod: string,
    activeOps: VNextOp[],
): {summary: string; lines: string[]} {
    const lines = [
        `${scenarioName} is using the ${baselineMethod.replace(/_/g, " ")} baseline.`,
        baselineMethodSummary(baselineMethod),
    ];
    if (activeOps.length === 0) {
        lines.push("There are no active modeled overrides or drivers in this scenario right now.");
        return {summary: lines.join(" "), lines};
    }
    const modeledChanges = activeOps
        .slice()
        .sort((a, b) => a.created_at.localeCompare(b.created_at))
        .slice(0, 5)
        .map(describeScenarioOp)
        .join(" ");
    lines.push(`Active modeled changes (${activeOps.length}): ${modeledChanges}`);
    return {summary: lines.join(" "), lines};
}

const MONTH_OPTIONS = buildMonthOptions();
const DASHBOARD_EXPORT_OPTIONS = [
    {key: "ceo_summary", label: "CEO Summary"},
    {key: "xero_pnl", label: "P&L Summary"},
    {key: "cashflow_summary", label: "Cash Flow Summary"},
    {key: "bs_summary", label: "BS Summary"},
];
const DASHBOARD_EXPORT_LABELS = Object.fromEntries(
    DASHBOARD_EXPORT_OPTIONS.map((t) => [t.key, t.label]),
) as Record<string, string>;
const ENABLE_INTERNAL_3STATEMENT_TAB = true;
const ENABLE_MODELING_WORKBENCH_TAB = true;
const INTERNAL_FORECAST_SUMMARY_TAB: VNextTab = {
    id: "__internal_forecast_summary__",
    key: "forecast_summary",
    label: "Forecast Summary",
    ordinal: 999,
    is_visible: true,
    config: {internal_only: true},
    has_context: true,
};
const INTERNAL_MODELING_WORKBENCH_TAB: VNextTab = {
    id: "__internal_modeling_workbench__",
    key: "modeling_workbench",
    label: "Modeling Workbench",
    ordinal: 998,
    is_visible: true,
    config: {internal_only: true},
    has_context: true,
};
const INTERNAL_DRIVERS_ASSUMPTIONS_TAB: VNextTab = {
    id: "__internal_drivers_assumptions__",
    key: "drivers_assumptions",
    label: "Drivers & Assumptions",
    ordinal: 997,
    is_visible: true,
    config: {internal_only: true},
    has_context: true,
};

// Period/compare/as-of controls: point-in-time report tabs only
const FINANCIAL_TABS = new Set(["ceo_summary", "xero_pnl", "cashflow_summary", "bs_summary"]);
// Rolling actuals/forecast selectors: forecast editing and read surfaces
const FORECAST_TABS = new Set(["cashflow_explorer"]);
// Dashboard summary tabs: actuals-only until workbench cutover
const DASHBOARD_SUMMARY_TABS = new Set(["summary", "ceo_summary", "xero_pnl", "cashflow_summary", "bs_summary", "bookings_summary"]);
const SCENARIO_EDIT_TABS = new Set<string>();
const SUPERSEDED_WORKBENCH_TABS = new Set(["pnl_explorer", "bs"]);

// ── Tab component registry ────────────────────────────────────
// To add a new tab, just import the component and add one line here.
// Any tab key registered in the backend but not listed here will
// render the PlaceholderTab automatically.
const TAB_COMPONENTS: Record<string, React.FC<TabProps>> = {
    summary: SummaryTab,
    ceo_summary: CEOSummaryV2Tab,
    saas_summary: SaaSSummaryTab,
    audit: AuditTab,
    xero_pnl: XeroPnLTab,
    cashflow_summary: CashFlowSummaryTab,
    bs: BalanceSheetTab,
    bs_summary: BalanceSheetSummaryTab,
    forecast_summary: ForecastSummaryTab,
    modeling_workbench: ModelingWorkbenchTab,
    drivers_assumptions: DriversAssumptionsTab,
    ar: ARTab,
    revenue: RevenueTab,
    pricing: PricingTab,
    // variance: VarianceTab, // hidden for now
    // health: HealthCheckTab, // hidden — checks validate VNext engine, not current actuals surfaces
    knowledge: KnowledgeTab,
    insights: InsightsTab,
    pnl_explorer: PnLExplorerTab,
    cashflow_explorer: CashFlowExplorerTab,
    transactions: TransactionsTab,
    ops: OpsTab,
    sandbox: SandboxTab,
    contact_mapping: ContactMappingTab,
    account_mapping: AccountMappingTab,
    bookings_summary: BookingsSummaryTab,
    data_sync: DataSyncTab,
    instructions: InstructionsTab,
};

/** Tab key groups — order here controls display order; dividers placed between groups. */
const TAB_GROUP_KEYS: string[][] = [
    ["summary", "instructions", "ceo_summary", "saas_summary", "xero_pnl", "cashflow_summary", "bs_summary", "bookings_summary", "modeling_workbench", "drivers_assumptions", "forecast_summary"],
    ["cashflow_explorer", "ar"],
    // ["revenue"], // hidden until revenue tab data source is stabilized
    // ["variance"], // hidden for now
    ["transactions", "knowledge", "sandbox", "ops"],
    // ["insights"], // hidden: commentary tab not currently used
    ["contact_mapping", "account_mapping", "data_sync"],
];

function formatRelativeTime(iso: string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

type SyncPhase = "idle" | "seeding" | "syncing" | "done" | "error";
type PdfWriter = {
    addPage: (format?: string | number[], orientation?: "l" | "p" | "landscape" | "portrait") => unknown;
    addImage: (
        imageData: string,
        format: string,
        x: number,
        y: number,
        width: number,
        height: number,
        alias?: string,
        compression?: "NONE" | "FAST" | "MEDIUM" | "SLOW",
    ) => unknown;
};

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

const COLOR_NORMALIZER_CANVAS = document.createElement("canvas");
const COLOR_NORMALIZER_CONTEXT = COLOR_NORMALIZER_CANVAS.getContext("2d");

/**
 * Replace all oklch()/oklab() color functions in CSS text with browser-resolved
 * hex/rgba equivalents so html2canvas (which can't parse oklch) works.
 */
function replaceOklchInCssText(cssText: string): string {
    const ctx = COLOR_NORMALIZER_CONTEXT;
    if (!ctx) return cssText;
    let out = cssText.replace(/oklch\([^)]+\)/gi, (match) => {
        try {
            ctx.fillStyle = "#ff00ff"; // sentinel (not black, so oklch(0 0 0) works)
            ctx.fillStyle = match;
            const result = ctx.fillStyle;
            return result !== "#ff00ff" ? result : "rgb(15, 23, 42)";
        } catch {
            return "rgb(15, 23, 42)";
        }
    }).replace(/oklab\([^)]+\)/gi, (match) => {
        try {
            ctx.fillStyle = "#ff00ff";
            ctx.fillStyle = match;
            const result = ctx.fillStyle;
            return result !== "#ff00ff" ? result : "rgb(15, 23, 42)";
        } catch {
            return "rgb(15, 23, 42)";
        }
    });

    // color-mix + colorspace tokens can still break parser in some html2canvas versions.
    out = out.replace(/color-mix\([^)]+\)/gi, "rgb(15, 23, 42)");
    out = out.replace(/\bin\s+oklch\b/gi, "in srgb");
    out = out.replace(/\bin\s+oklab\b/gi, "in srgb");

    // Final hard guard: ensure unsupported tokens are gone.
    out = out.replace(/oklch\([^)]+\)/gi, "rgb(15, 23, 42)");
    out = out.replace(/oklab\([^)]+\)/gi, "rgb(15, 23, 42)");
    return out;
}

/** Rewrite all oklch/oklab in <style> elements of a cloned document. */
function rewriteOklchInDocument(doc: Document): void {
    for (const style of Array.from(doc.querySelectorAll("style"))) {
        const text = style.textContent;
        if (text && (text.includes("oklch(") || text.includes("oklab("))) {
            style.textContent = replaceOklchInCssText(text);
        }
    }
}

function stripStylesheetsFromDocument(doc: Document): void {
    for (const node of Array.from(doc.querySelectorAll("style, link[rel='stylesheet']"))) {
        node.remove();
    }
}

function sanitizeInlineStylesInDocument(doc: Document): void {
    for (const el of Array.from(doc.querySelectorAll<HTMLElement>("[style]"))) {
        const style = el.getAttribute("style");
        if (!style) continue;
        if (style.includes("oklch(") || style.includes("oklab(")) {
            el.setAttribute("style", replaceOklchInCssText(style));
        }
    }

    // Some chart libs/SVG nodes store colors in attributes, not style text.
    const attrs = ["fill", "stroke", "stop-color", "color", "bgcolor"];
    for (const el of Array.from(doc.querySelectorAll<HTMLElement>("*"))) {
        for (const attr of attrs) {
            const value = el.getAttribute(attr);
            if (!value) continue;
            if (value.includes("oklch(") || value.includes("oklab(") || value.includes("color-mix(")) {
                el.setAttribute(attr, replaceOklchInCssText(value));
            }
        }
    }
}

function buildSanitizedCssSnapshot(): string {
    const chunks: string[] = [];
    for (const sheet of Array.from(document.styleSheets)) {
        try {
            const rules = (sheet as CSSStyleSheet).cssRules;
            for (const rule of Array.from(rules)) {
                chunks.push(rule.cssText);
            }
        } catch {
            // Skip cross-origin or inaccessible stylesheets.
        }
    }
    return replaceOklchInCssText(chunks.join("\n"));
}

function applySanitizedCssToDocument(doc: Document, cssText: string): void {
    stripStylesheetsFromDocument(doc);
    const style = doc.createElement("style");
    style.setAttribute("data-export-sanitized", "true");
    style.textContent = cssText;
    doc.head.appendChild(style);
}

function clonePanelForPrint(source: HTMLElement): HTMLElement {
    const clone = source.cloneNode(true) as HTMLElement;
    clone.removeAttribute("hidden");
    clone.style.display = "block";
    clone.style.overflow = "visible";
    clone.style.maxHeight = "none";
    clone.style.height = "auto";
    clone.style.minHeight = "auto";

    const sourceCanvases = source.querySelectorAll("canvas");
    const clonedCanvases = clone.querySelectorAll("canvas");
    sourceCanvases.forEach((canvas, idx) => {
        const target = clonedCanvases[idx];
        if (!target) return;
        try {
            const dataUrl = canvas.toDataURL("image/png");
            const img = document.createElement("img");
            img.src = dataUrl;
            img.alt = "Chart";
            const width = canvas.clientWidth || canvas.width;
            const height = canvas.clientHeight || canvas.height;
            if (width) img.style.width = `${width}px`;
            if (height) img.style.height = `${height}px`;
            img.style.maxWidth = "100%";
            target.replaceWith(img);
        } catch {
            // Keep original canvas if toDataURL fails.
        }
    });

    const all = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>("*"))];
    all.forEach((el) => {
        if (el.hasAttribute("hidden")) el.removeAttribute("hidden");
        const rawClassName = (el as {className?: unknown}).className;
        const cls =
            typeof rawClassName === "string"
                ? rawClassName
                : (
                    rawClassName &&
                    typeof rawClassName === "object" &&
                    "baseVal" in (rawClassName as Record<string, unknown>)
                        ? ((rawClassName as {baseVal?: string}).baseVal || "")
                        : (el.getAttribute("class") || "")
                );
        if (
            cls.includes("overflow-") ||
            cls.includes("max-h-") ||
            cls.includes("h-[") ||
            cls.includes("min-h-0")
        ) {
            el.style.overflow = "visible";
            el.style.maxHeight = "none";
            el.style.height = "auto";
            el.style.minHeight = "auto";
        }
        const inlinePos = (el.style.position || "").toLowerCase();
        if (inlinePos === "sticky" || inlinePos === "fixed") {
            el.style.position = "static";
        }
    });
    return clone;
}

function addCanvasToPdfPages(
    pdf: PdfWriter,
    canvas: HTMLCanvasElement,
    options: {
        contentWidthMm: number;
        contentHeightMm: number;
        marginXmm: number;
        marginYmm: number;
        addPageBeforeFirstSlice: boolean;
    },
): void {
    const {contentWidthMm, contentHeightMm, marginXmm, marginYmm, addPageBeforeFirstSlice} = options;
    const mmPerPx = contentWidthMm / Math.max(1, canvas.width);
    const maxSlicePx = Math.max(1, Math.floor(contentHeightMm / mmPerPx));

    let y = 0;
    let firstSlice = true;
    while (y < canvas.height) {
        if ((firstSlice && addPageBeforeFirstSlice) || (!firstSlice)) {
            pdf.addPage("a4", "landscape");
        }

        const sliceHeightPx = Math.min(maxSlicePx, canvas.height - y);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext("2d");
        if (!ctx) break;
        ctx.drawImage(canvas, 0, y, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

        const sliceHeightMm = sliceHeightPx * mmPerPx;
        pdf.addImage(
            sliceCanvas.toDataURL("image/png"),
            "PNG",
            marginXmm,
            marginYmm,
            contentWidthMm,
            sliceHeightMm,
            undefined,
            "FAST",
        );

        y += sliceHeightPx;
        firstSlice = false;
    }
}

interface WorkspaceShellProps {
    /** Show only these tab keys (if set, excludeTabs is ignored). */
    onlyTabs?: string[];
    /** Hide these tab keys (ignored when onlyTabs is set). */
    excludeTabs?: string[];
    /** Override page title. */
    pageTitle?: string;
    /** Default active tab key (falls back to first visible tab). */
    defaultTab?: string;
}

export function WorkspaceShell({onlyTabs, excludeTabs, pageTitle, defaultTab}: WorkspaceShellProps = {}) {
    const {mutate} = useSWRConfig();
    const {getIntegration} = useIntegrations();
    const xero = getIntegration("xero");
    const tenantId = xero?.xeroTenantSelected?.tenantId ?? xero?.xeroTenantDefault?.tenantId;
    const {workspace, tabs, scenarios, isLoading, error, refresh} = useVNextWorkspace(tenantId);
    const [syncPhase, setSyncPhase] = useState<SyncPhase>("idle");
    const {syncStatus} = useVNextSyncStatus(syncPhase === "syncing");
    const storageKey = pageTitle === "Dashboard" ? "vnext-dashboard-tab" : "vnext-financial-tab";
    const [activeTab, setActiveTab] = useState<string>(() => {
        try {
            return localStorage.getItem(storageKey) || defaultTab || "";
        } catch { return defaultTab || ""; }
    });
    const [seedError, setSeedError] = useState<string | null>(null);
    const [seedWarning] = useState<string | null>(null);
    const [seedResult, setSeedResult] = useState<string | null>(null);
    const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [exporting, setExporting] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [showDashboardTour, setShowDashboardTour] = useState(false);
    const [showFinancialModelTour, setShowFinancialModelTour] = useState(false);
    const [pdfTabs, setPdfTabs] = useState<string[]>(DASHBOARD_EXPORT_OPTIONS.map((t) => t.key));
    const [autoTriggerSyncAll, setAutoTriggerSyncAll] = useState(false);
    const [txnFilters, setTxnFilters] = useState<LedgerFilters | null>(null);
    const defaultScenario = scenarios.find((s) => s.is_default) ?? scenarios[0];
    const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
    const activeScenario = scenarios.find((s) => s.id === selectedScenarioId) ?? defaultScenario;
    const {ops} = useVNextOps(workspace?.id ?? null, activeScenario?.id);
    const [baselineMethod, setBaselineMethod] = useState(defaultScenario?.baseline_method ?? "avg_6m");

    // Period controls — separate per tab group
    const summaryActualMonths = 12;
    const [pnlActualMonths, setPnlActualMonths] = useState(12);
    const [pnlForecastMonths, setPnlForecastMonths] = useState(12);
    const [cfActualMonths, setCfActualMonths] = useState(3);
    const [cfForecastMonths, setCfForecastMonths] = useState(12);

    const [bsActualMonths, setBsActualMonths] = useState(3);
    const [bsForecastMonths, setBsForecastMonths] = useState(12);

    // Period / comparison controls
    const [period, setPeriod] = useState<PeriodPreset>("YTD");
    const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0].value);
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [compare, setCompare] = useState<"none" | "prev" | "yoy">("yoy");
    const [currentMonth, setCurrentMonth] = useState<string>("");

    const visibleTabs = useMemo(() => {
        const baseTabs = tabs.filter((t) => t.is_visible);
        const withForecastSummary = ENABLE_INTERNAL_3STATEMENT_TAB && !baseTabs.some((t) => t.key === INTERNAL_FORECAST_SUMMARY_TAB.key)
            ? [...baseTabs, INTERNAL_FORECAST_SUMMARY_TAB]
            : baseTabs;
        const withWorkbench = ENABLE_MODELING_WORKBENCH_TAB && !withForecastSummary.some((t) => t.key === INTERNAL_MODELING_WORKBENCH_TAB.key)
            ? [...withForecastSummary, INTERNAL_MODELING_WORKBENCH_TAB]
            : withForecastSummary;
        const withInternalTabs = ENABLE_MODELING_WORKBENCH_TAB && !withWorkbench.some((t) => t.key === INTERNAL_DRIVERS_ASSUMPTIONS_TAB.key)
            ? [...withWorkbench, INTERNAL_DRIVERS_ASSUMPTIONS_TAB]
            : withWorkbench;
        const withoutSupersededTabs = withInternalTabs.filter((t) => !SUPERSEDED_WORKBENCH_TABS.has(t.key));
        // Apply tab filtering
        if (onlyTabs) return withoutSupersededTabs.filter((t) => onlyTabs.includes(t.key));
        if (excludeTabs) return withoutSupersededTabs.filter((t) => !excludeTabs.includes(t.key));
        return withoutSupersededTabs;
    }, [tabs, onlyTabs, excludeTabs]);
    const isDashboardView = pageTitle === "Dashboard";
    const dashboardExportTabs = useMemo(
        () => DASHBOARD_EXPORT_OPTIONS.filter((opt) => visibleTabs.some((t) => t.key === opt.key)),
        [visibleTabs],
    );

    // Fiscal year start month (derived from Xero tenant fyEndMonth)
    const fyEndMonth = xero?.xeroTenantSelected?.fyEndMonth ?? xero?.xeroTenantDefault?.fyEndMonth;
    const fyStartMonth = fyEndMonth ? (fyEndMonth % 12) + 1 : undefined;

    // When period is FY, bump forecast months to cover remaining FY months.
    // Uses the As-of anchor month (not today) so Full Year split is correct.
    const fyEffectiveForecast = useMemo(() => {
        if (period !== "FY") return null;
        const anchorDate = currentMonth
            ? new Date(currentMonth + "-01")
            : new Date();
        const anchorM = anchorDate.getMonth() + 1;
        const fyStart = fyStartMonth ?? 1;
        const monthsIntoFY = anchorM >= fyStart
            ? anchorM - fyStart + 1
            : 12 - fyStart + anchorM + 1;
        const remaining = 12 - monthsIntoFY;
        return remaining + 1; // +1 to include anchor month in forecast if needed
    }, [period, fyStartMonth, currentMonth]);

    // Detect running sync on page load (only if started recently — ignore stale "running" logs)
    useEffect(() => {
        if (syncStatus?.status === "running" && syncPhase === "idle") {
            const startedAt = syncStatus.started_at ? new Date(syncStatus.started_at).getTime() : 0;
            const ageMinutes = (Date.now() - startedAt) / 60_000;
            // Only auto-enter syncing if started within the last 10 minutes
            if (ageMinutes < 10) {
                setSyncPhase("syncing");
            }
        }
    }, [syncStatus?.status, syncStatus?.started_at, syncPhase]);

    // SyncPhase state machine: syncing → done when sync finished + snapshot ready
    const syncingStartRef = useRef<number>(0);
    useEffect(() => {
        if (syncPhase === "syncing" && syncingStartRef.current === 0) {
            syncingStartRef.current = Date.now();
        } else if (syncPhase !== "syncing") {
            syncingStartRef.current = 0;
        }
    }, [syncPhase]);

    useEffect(() => {
        if (syncPhase !== "syncing") return;
        if (!syncStatus) return;

        if (syncStatus.status === "error") {
            setSyncPhase("error");
            setSeedError("Sync failed — check logs for details");
            return;
        }

        const syncSucceeded = syncStatus.status === "success" || syncStatus.status === "partial";

        if (syncSucceeded && syncStatus.snapshot_ready) {
            setSyncPhase("done");
            setSeedResult(
                `Synced ${syncStatus.records_synced ?? 0} records — snapshot ready`,
            );
            // Invalidate all workspace data
            refresh();
            if (workspace) {
                mutate((key: unknown) => typeof key === "string" && key.startsWith(`/api/model-vnext/workspace/${workspace.id}/`));
            }
            // Auto-reset to idle after 8s
            doneTimerRef.current = setTimeout(() => setSyncPhase("idle"), 8000);
            return;
        }

        // Fallback: if sync succeeded but snapshot_ready never arrives, complete after 30s
        if (syncSucceeded && !syncStatus.snapshot_ready) {
            const elapsed = Date.now() - (syncingStartRef.current || Date.now());
            if (elapsed > 30_000) {
                setSyncPhase("done");
                setSeedResult(
                    `Synced ${syncStatus.records_synced ?? 0} records`,
                );
                refresh();
                if (workspace) {
                    mutate((key: unknown) => typeof key === "string" && key.startsWith(`/api/model-vnext/workspace/${workspace.id}/`));
                }
                doneTimerRef.current = setTimeout(() => setSyncPhase("idle"), 8000);
            }
            return;
        }

        // Safety valve: if stuck in "syncing" for > 5 minutes, reset to idle
        const elapsed = Date.now() - (syncingStartRef.current || Date.now());
        if (elapsed > 5 * 60_000) {
            setSyncPhase("idle");
        }
    }, [syncPhase, syncStatus, refresh, mutate, workspace]);

    // Cleanup done timer on unmount
    useEffect(() => {
        return () => {
            if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
        };
    }, []);

    useEffect(() => {
        document.title = pageTitle ? `${pageTitle} - Pecunio Agents` : "Financial Model - Pecunio Agents";
    }, [pageTitle]);

    // Sync baseline method from scenario data
    useEffect(() => {
        if (defaultScenario?.baseline_method) {
            setBaselineMethod(defaultScenario.baseline_method);
        }
    }, [defaultScenario?.baseline_method]);

    const handleBaselineChange = useCallback(
        async (method: string) => {
            if (!workspace) return;
            setBaselineMethod(method);
            try {
                await patchScenario(workspace.id, {baseline_method: method});
                refresh();
                // Invalidate all tab caches so they re-render with new baseline
                mutate((key: unknown) => typeof key === "string" && key.startsWith(`/api/model-vnext/workspace/${workspace.id}/`));
            } catch {
                // Revert on failure
                setBaselineMethod(defaultScenario?.baseline_method ?? "avg_6m");
            }
        },
        [workspace, defaultScenario, refresh, mutate],
    );



    const handleExportExcel = useCallback(async () => {
        if (!workspace) return;
        setExporting(true);
        try {
            const csrfToken = (await import("js-cookie")).default.get("csrftoken") || "";
            const resp = await fetch(`/api/model-vnext/workspace/${workspace.id}/export/excel/`, {
                method: "POST",
                headers: {"Content-Type": "application/json", "X-CSRFToken": csrfToken},
                credentials: "include",
                body: JSON.stringify({months: 12, actual_months: 12, include_ledger: true, include_tieout: true, include_checks: true}),
            });
            if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const disposition = resp.headers.get("Content-Disposition") || "";
            const match = disposition.match(/filename="?([^"]+)"?/);
            a.download = match?.[1] || `VNext_${workspace.name.replace(/\s+/g, "_")}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setSeedError(e instanceof Error ? e.message : "Export failed");
        } finally {
            setExporting(false);
        }
    }, [workspace]);

    useEffect(() => {
        if (!isDashboardView) return;
        const available = new Set(dashboardExportTabs.map((t) => t.key));
        setPdfTabs((prev) => {
            const kept = prev.filter((k) => available.has(k));
            if (kept.length > 0) return kept;
            return dashboardExportTabs.map((t) => t.key);
        });
    }, [isDashboardView, dashboardExportTabs]);

    const handleTogglePdfTab = useCallback((tabKey: string, checked: boolean) => {
        setPdfTabs((prev) => {
            if (checked) {
                if (prev.includes(tabKey)) return prev;
                return [...prev, tabKey];
            }
            if (prev.length <= 1) return prev;
            return prev.filter((k) => k !== tabKey);
        });
    }, []);

    const handleExportPdf = useCallback(async () => {
        if (!workspace || pdfTabs.length === 0) return;
        setExportingPdf(true);
        const originalTab = activeTab;
        const exportRoot = document.createElement("div");
        try {
            const fontReadyPromise = document.fonts?.ready ?? Promise.resolve();
            await fontReadyPromise;
            const {default: html2canvas} = await import("html2canvas");
            const {jsPDF} = await import("jspdf");
            const sanitizedCssSnapshot = buildSanitizedCssSnapshot();

            const pdf = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4",
                compress: true,
            });
            const marginXmm = 8;
            const marginYmm = 8;
            const pageWidthMm = 297;
            const pageHeightMm = 210;
            const contentWidthMm = pageWidthMm - marginXmm * 2;
            const contentHeightMm = pageHeightMm - marginYmm * 2;

            exportRoot.setAttribute("aria-hidden", "true");
            exportRoot.style.position = "fixed";
            exportRoot.style.left = "-100000px";
            exportRoot.style.top = "0";
            exportRoot.style.width = "1600px";
            exportRoot.style.background = "#ffffff";
            exportRoot.style.padding = "0";
            exportRoot.style.zIndex = "-1";
            document.body.appendChild(exportRoot);

            let hasTabContent = false;
            for (let idx = 0; idx < pdfTabs.length; idx++) {
                const tabKey = pdfTabs[idx];
                if (activeTab !== tabKey) {
                    setActiveTab(tabKey);
                    await sleep(700);
                }
                await sleep(80);
                const panel = document.querySelector<HTMLElement>(
                    `[data-export-tab="${tabKey}"]`,
                );
                if (!panel) continue;
                hasTabContent = true;

                const exportWidthPx = Math.max(1200, Math.ceil(panel.getBoundingClientRect().width || 1200));
                exportRoot.style.width = `${exportWidthPx}px`;
                exportRoot.innerHTML = "";

                const section = document.createElement("section");
                section.style.width = `${exportWidthPx}px`;
                section.style.background = "#ffffff";
                section.style.padding = "20px";
                section.style.boxSizing = "border-box";

                const title = document.createElement("h1");
                title.textContent = DASHBOARD_EXPORT_LABELS[tabKey] || tabKey;
                title.style.margin = "0 0 14px";
                title.style.fontSize = "26px";
                title.style.fontWeight = "700";
                title.style.color = "#209DB3";
                section.appendChild(title);

                const cloned = clonePanelForPrint(panel);
                cloned.style.width = "100%";
                section.appendChild(cloned);
                exportRoot.appendChild(section);

                let canvas: HTMLCanvasElement;
                try {
                    // First pass: highest-fidelity render path.
                    canvas = await html2canvas(section, {
                        backgroundColor: "#ffffff",
                        scale: 2,
                        useCORS: true,
                        logging: false,
                        width: exportWidthPx,
                        windowWidth: exportWidthPx,
                        foreignObjectRendering: true,
                    });
                } catch {
                    // Fallback: sanitize unsupported CSS color functions for parser compatibility.
                    canvas = await html2canvas(section, {
                        backgroundColor: "#ffffff",
                        scale: 2,
                        useCORS: true,
                        logging: false,
                        width: exportWidthPx,
                        windowWidth: exportWidthPx,
                        onclone: (clonedDoc: Document) => {
                            applySanitizedCssToDocument(clonedDoc, sanitizedCssSnapshot);
                            rewriteOklchInDocument(clonedDoc);
                            sanitizeInlineStylesInDocument(clonedDoc);
                        },
                    });
                }

                addCanvasToPdfPages(pdf, canvas, {
                    contentWidthMm,
                    contentHeightMm,
                    marginXmm,
                    marginYmm,
                    addPageBeforeFirstSlice: idx > 0,
                });
            }

            if (!hasTabContent) {
                throw new Error("No dashboard tab content found to export.");
            }

            const safeWorkspaceName = workspace.name.replace(/[^a-zA-Z0-9_-]+/g, "_");
            const filename = `Dashboard_${safeWorkspaceName}_${new Date().toISOString().slice(0, 10)}.pdf`;
            pdf.save(filename);
        } catch (e) {
            setSeedError(e instanceof Error ? e.message : "PDF export failed");
        } finally {
            exportRoot.remove();
            if (originalTab) setActiveTab(originalTab);
            setExportingPdf(false);
        }
    }, [
        workspace,
        pdfTabs,
        activeTab,
    ]);

    useEffect(() => {
        if (visibleTabs.length > 0 && (!activeTab || !visibleTabs.some((t) => t.key === activeTab))) {
            const saved = (() => { try { return localStorage.getItem(storageKey) || ""; } catch { return ""; } })();
            const target = saved && visibleTabs.some((t) => t.key === saved)
                ? saved
                : defaultTab && visibleTabs.some((t) => t.key === defaultTab)
                    ? defaultTab
                    : visibleTabs[0].key;
            setActiveTab(target);
        }
    }, [visibleTabs, activeTab, defaultTab, storageKey]);

    // Persist active tab to localStorage
    useEffect(() => {
        if (activeTab) {
            try { localStorage.setItem(storageKey, activeTab); } catch { /* ignore */ }
        }
    }, [activeTab, storageKey]);

    // Refresh all vnext data when agent commits changes (debounced to prevent storm)
    const invalidationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        const handler = () => {
            if (!workspace) return;
            // Debounce: if multiple tool results arrive quickly, only invalidate once
            if (invalidationTimer.current) clearTimeout(invalidationTimer.current);
            invalidationTimer.current = setTimeout(() => {
                refresh();
                mutate((key: unknown) => typeof key === "string" && key.startsWith(`/api/model-vnext/workspace/${workspace.id}/`));
            }, 2000);
        };
        window.addEventListener("vnext-data-updated", handler);
        return () => {
            window.removeEventListener("vnext-data-updated", handler);
            if (invalidationTimer.current) clearTimeout(invalidationTimer.current);
        };
    }, [workspace, refresh, mutate]);

    // Handle vnext-ui-action events from agent (tab switches, period changes, etc.)
    useEffect(() => {
        const handler = (e: Event) => {
            const d = (e as CustomEvent).detail;
            if (d?.tab && visibleTabs.some((t) => t.key === d.tab)) setActiveTab(d.tab);
            if (d?.period) setPeriod(d.period);
            if (d?.month) setSelectedMonth(d.month);
            if (d?.compare) setCompare(d.compare);
            if (d?.actual_months != null) { setPnlActualMonths(d.actual_months); setCfActualMonths(d.actual_months); setBsActualMonths(d.actual_months); }
            if (d?.forecast_months != null) { setPnlForecastMonths(d.forecast_months); setCfForecastMonths(d.forecast_months); setBsForecastMonths(d.forecast_months); }
            if (d?.custom_from) setCustomFrom(d.custom_from);
            if (d?.custom_to) setCustomTo(d.custom_to);
            if (d?.current_month) setCurrentMonth(d.current_month === "auto" ? "" : d.current_month);
        };
        window.addEventListener("vnext-ui-action", handler);
        return () => window.removeEventListener("vnext-ui-action", handler);
    }, [visibleTabs]);

    // Handle "Open in Transactions" from ProvenancePanel
    useEffect(() => {
        const handleOpenTxn = (e: CustomEvent<{filters: LedgerFilters}>) => {
            setTxnFilters(e.detail.filters);
            setActiveTab("transactions");
        };
        window.addEventListener("vnext-open-transactions", handleOpenTxn as EventListener);
        return () => window.removeEventListener("vnext-open-transactions", handleOpenTxn as EventListener);
    }, []);

    // Clear txnFilters when navigating away from transactions
    useEffect(() => {
        if (activeTab !== "transactions") setTxnFilters(null);
    }, [activeTab]);

    // Notify FloatingChat which tab is active so it resolves the correct agent
    useEffect(() => {
        if (activeTab) {
            window.dispatchEvent(new CustomEvent("dashboard-tab-changed", {detail: {tab: activeTab}}));
        }
    }, [activeTab]);

    const activeOps = useMemo(() => ops.filter((op) => op.is_active), [ops]);
    const scenarioCommentary = useMemo(
        () => activeScenario
            ? buildScenarioCommentary(activeScenario.name, activeScenario.baseline_method, activeOps)
            : null,
        [activeScenario, activeOps],
    );

    // Send workspace context to ChatInterface so agent has full VNext context
    useEffect(() => {
        if (workspace && activeScenario && activeTab) {
            window.dispatchEvent(new CustomEvent("dashboard-data-loaded", {
                detail: {
                    vnext: true,
                    workspaceId: workspace.id,
                    workspaceName: workspace.name,
                    scenarioId: activeScenario.id,
                    scenarioName: activeScenario.name,
                    baselineMethod: activeScenario.baseline_method,
                    activeOpsCount: activeOps.length,
                    activeOpsSummary: activeOps.map(describeScenarioOp),
                    scenarioCommentary: scenarioCommentary?.summary ?? "",
                    scenarioCommentaryLines: scenarioCommentary?.lines ?? [],
                    activeTab,
                    tabs: visibleTabs.map((t) => t.key),
                },
            }));
        }
    }, [workspace, activeScenario, activeOps, activeTab, scenarioCommentary, visibleTabs]);

    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2Icon className="size-6 animate-spin text-muted-foreground"/>
                <span className="ml-2 text-muted-foreground">Loading workspace...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-64 items-center justify-center">
                <AlertCircleIcon className="size-6 text-red-500"/>
                <span className="ml-2 text-red-600">{error}</span>
            </div>
        );
    }

    if (!workspace) return null;

    return (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            {/* Header — pinned */}
            <div className="shrink-0 px-6 pt-6 pb-2">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-3" data-tour="dashboard-title">
                            <h1 className="text-3xl font-bold">{pageTitle || workspace.name}</h1>
                        </div>
                        {workspace.description && (
                            <p className="text-muted-foreground mt-1">{workspace.description}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {syncStatus?.has_synced && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="flex flex-col items-end gap-0.5">
                                            <Badge
                                                variant="outline"
                                                className={
                                                    syncStatus.status === "error"
                                                        ? "border-red-400 text-red-600 text-xs"
                                                        : syncStatus.status === "skipped"
                                                          ? "border-slate-400 text-slate-500 text-xs"
                                                          : syncStatus.status === "running"
                                                            ? "border-blue-400 text-blue-600 text-xs"
                                                            : syncStatus.status === "partial" || syncStatus.is_stale || syncStatus.has_incomplete_full
                                                              ? "border-amber-400 text-amber-600 text-xs"
                                                              : "border-green-400 text-green-600 text-xs"
                                                }
                                            >
                                                {syncStatus.status === "error"
                                                    ? "Sync Error"
                                                    : syncStatus.status === "skipped"
                                                      ? "Skipped"
                                                      : syncStatus.status === "running"
                                                        ? "Syncing..."
                                                        : syncStatus.status === "partial" || syncStatus.has_incomplete_full
                                                          ? "Partial Sync"
                                                          : syncStatus.is_stale
                                                            ? "Stale"
                                                            : "Synced"}
                                            </Badge>
                                            {syncStatus.completed_at && (
                                                <span className="text-[10px] text-muted-foreground leading-tight">
                                                    {formatRelativeTime(syncStatus.completed_at)}
                                                </span>
                                            )}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                        <p className="text-xs font-medium">
                                            {syncStatus.completed_at
                                                ? `Last sync: ${new Date(syncStatus.completed_at).toLocaleString()}`
                                                : "Sync in progress"}
                                        </p>
                                        {syncStatus.records_synced != null && (
                                            <p className="text-xs">{syncStatus.records_synced.toLocaleString()} records synced</p>
                                        )}
                                        {syncStatus.pages_fetched != null && (
                                            <p className="text-xs">{syncStatus.pages_fetched} pages fetched{syncStatus.is_complete === false ? " (incomplete)" : ""}</p>
                                        )}
                                        {syncStatus.has_incomplete_full && (
                                            <p className="text-xs text-amber-600 mt-1">Full sync incomplete — click Sync to resume from where it left off.</p>
                                        )}
                                        {syncStatus.status === "error" && syncStatus.error_detail && (
                                            <p className="text-xs text-red-500 mt-1 break-words">{syncStatus.error_detail.slice(0, 200)}</p>
                                        )}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-[#5BF6C8] text-[#209DB3] hover:bg-[#209DB3]/10"
                            onClick={() => {
                                if (isDashboardView) setShowDashboardTour(true);
                                else setShowFinancialModelTour(true);
                            }}
                            data-tour="dashboard-tour-button"
                        >
                            <CompassIcon className="mr-2 size-4" />
                            Take a Tour
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-[#209DB3] border-[#209DB3] hover:bg-[#209DB3] hover:text-white"
                                    disabled={exporting || exportingPdf}
                                >
                                    {exporting || exportingPdf ? (
                                        <Loader2Icon className="mr-2 size-4 animate-spin" />
                                    ) : (
                                        <DownloadIcon className="mr-2 size-4" />
                                    )}
                                    {exporting || exportingPdf ? "Exporting..." : "Export"}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-72">
                                <DropdownMenuLabel>Export Format</DropdownMenuLabel>
                                <DropdownMenuItem
                                    onClick={handleExportExcel}
                                    disabled={exporting || exportingPdf}
                                >
                                    Export Excel (.xlsx)
                                </DropdownMenuItem>

                                {isDashboardView && dashboardExportTabs.length > 0 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuLabel>PDF Tabs</DropdownMenuLabel>
                                        {dashboardExportTabs.map((tab) => (
                                            <DropdownMenuCheckboxItem
                                                key={tab.key}
                                                checked={pdfTabs.includes(tab.key)}
                                                onCheckedChange={(checked) =>
                                                    handleTogglePdfTab(tab.key, checked === true)
                                                }
                                            >
                                                {tab.label}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={handleExportPdf}
                                            disabled={pdfTabs.length === 0 || exporting || exportingPdf}
                                        >
                                            Export PDF (.pdf)
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
                {seedError && (
                    <p className="text-sm text-red-600 mt-1">{seedError}</p>
                )}
                {seedWarning && (
                    <p className="flex items-center gap-1.5 text-sm text-amber-600 mt-1">
                        <AlertTriangleIcon className="size-4 shrink-0"/>
                        {seedWarning}
                    </p>
                )}
                {seedResult && (
                    <p className="text-sm text-green-600 mt-1">{seedResult}</p>
                )}
            </div>

            {/* Quality Checks Status — pinned */}
            <div className="shrink-0 px-6 pb-2" data-tour="dashboard-checks-strip">
                <ChecksStatusStrip workspaceId={workspace.id} />
            </div>

            {/* Tab-driven content — fills remaining height */}
            {visibleTabs.length > 0 ? (
                <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v !== "data_sync") setAutoTriggerSyncAll(false); }} className="flex-1 min-h-0 min-w-0 flex flex-col px-6 overflow-hidden">
                    {/* Tab bar + period controls — pinned */}
                    <div className="shrink-0 pb-3">
                        {(() => {
                            const orderedTabs = TAB_GROUP_KEYS.flatMap(gk => gk.map(k => visibleTabs.find(t => t.key === k)).filter((t): t is VNextTab => !!t));
                            const mid = Math.ceil(orderedTabs.length / 2);
                            const row1 = orderedTabs.slice(0, mid);
                            const row2 = orderedTabs.slice(mid);
                            return (
                                <TabsList className="border border-[#5BF6C8] rounded-lg h-auto flex flex-col gap-0 w-full p-1" data-tour="dashboard-tab-bar">
                                    <div className="flex w-full">
                                        {row1.map((tab) => (
                                            <TabsTrigger key={tab.key} value={tab.key} data-tour={`dashboard-tab-trigger-${tab.key}`} className="data-[state=active]:bg-[#209DB3] data-[state=active]:text-white px-2 py-1 text-xs flex-1">
                                                <span className="flex items-center gap-1.5">
                                                    {tab.label}
                                                    {tab.has_context && <span className="w-1.5 h-1.5 rounded-full bg-[#209DB3] inline-block" />}
                                                </span>
                                            </TabsTrigger>
                                        ))}
                                    </div>
                                    {row2.length > 0 && (
                                        <div className="flex w-full border-t border-[#5BF6C8]/20">
                                            {row2.map((tab) => (
                                                <TabsTrigger key={tab.key} value={tab.key} data-tour={`dashboard-tab-trigger-${tab.key}`} className="data-[state=active]:bg-[#209DB3] data-[state=active]:text-white px-2 py-1 text-xs flex-1">
                                                    <span className="flex items-center gap-1.5">
                                                        {tab.label}
                                                        {tab.has_context && <span className="w-1.5 h-1.5 rounded-full bg-[#209DB3] inline-block" />}
                                                    </span>
                                                </TabsTrigger>
                                            ))}
                                        </div>
                                    )}
                                </TabsList>
                            );
                        })()}

                        {/* Period controls — visible for financial tabs */}
                        {FINANCIAL_TABS.has(activeTab) && (
                            <div className="space-y-2 mt-3" data-tour="dashboard-period-controls">
                                {/* Row 1: Period presets + Month picker + Comparison */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    {(["MTD", "QTD", "YTD", "FY", "MONTH", "CUSTOM"] as PeriodPreset[]).map((p) => (
                                        <button
                                            key={p}
                                            className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
                                                period === p
                                                    ? "bg-[#209DB3] text-white border-[#209DB3]"
                                                    : "border-[#5BF6C8] text-[#209DB3] hover:bg-[#209DB3]/10"
                                            }`}
                                            onClick={() => setPeriod(p)}
                                        >
                                            {p === "FY" ? "Full Year" : p === "MONTH" ? "Full Month" : p === "CUSTOM" ? "Custom" : p}
                                        </button>
                                    ))}

                                    <div className="w-px h-6 bg-border mx-1" />

                                    {period === "MONTH" && (
                                        <>
                                            <CalendarIcon className="size-3.5 text-muted-foreground" />
                                            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                                <SelectTrigger className="w-[140px] h-8 text-xs border-[#5BF6C8] text-[#209DB3]">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {MONTH_OPTIONS.map((opt) => (
                                                        <SelectItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </>
                                    )}

                                    {period === "CUSTOM" && (
                                        <>
                                            <span className="text-xs text-muted-foreground">From</span>
                                            <Input
                                                type="month"
                                                value={customFrom}
                                                onChange={(e) => setCustomFrom(e.target.value)}
                                                className="w-[140px] h-8 text-xs border-[#5BF6C8]"
                                            />
                                            <span className="text-xs text-muted-foreground">To</span>
                                            <Input
                                                type="month"
                                                value={customTo}
                                                onChange={(e) => setCustomTo(e.target.value)}
                                                className="w-[140px] h-8 text-xs border-[#5BF6C8]"
                                            />
                                        </>
                                    )}

                                    <div className="w-px h-6 bg-border mx-1" />

                                    <span className="text-xs text-muted-foreground">Compare</span>
                                    <Select value={compare} onValueChange={(v) => setCompare(v as "none" | "prev" | "yoy")}>
                                        <SelectTrigger className="w-[160px] h-8 text-xs border-[#5BF6C8] text-[#209DB3]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">No Comparison</SelectItem>
                                            <SelectItem value="prev">Previous Period</SelectItem>
                                            <SelectItem value="yoy">Year on Year</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    <div className="w-px h-6 bg-border mx-1" />

                                    <span className="text-xs text-muted-foreground">As of</span>
                                    <Select value={currentMonth || "auto"} onValueChange={(v) => setCurrentMonth(v === "auto" ? "" : v)}>
                                        <SelectTrigger className="w-[150px] h-8 text-xs border-[#5BF6C8] text-[#209DB3]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="auto">Current Month</SelectItem>
                                            {MONTH_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        )}

                        {/* Row 2: Baseline / Actuals / Forecast / 13-week — model tabs */}
                        {FORECAST_TABS.has(activeTab) && <div className="flex items-center gap-3 flex-wrap mt-3">
                            <span className="text-xs font-medium text-muted-foreground">Baseline</span>
                            <Select value={baselineMethod} onValueChange={handleBaselineChange}>
                                <SelectTrigger className="w-[150px] h-8 text-xs border-[#5BF6C8] text-[#209DB3]">
                                    <SelectValue placeholder="Baseline..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="avg_3m">3-Month Avg</SelectItem>
                                    <SelectItem value="avg_6m">6-Month Avg</SelectItem>
                                    <SelectItem value="avg_12m">12-Month Avg</SelectItem>
                                    <SelectItem value="last_month">Last Month</SelectItem>
                                    <SelectItem value="yoy_flat">YoY Flat</SelectItem>
                                    <SelectItem value="yoy_growth">YoY Growth</SelectItem>
                                </SelectContent>
                            </Select>

                            <div className="w-px h-6 bg-border mx-1" />

                            <span className="text-xs font-medium text-muted-foreground">Actuals</span>
                            <Select
                                value={String(
                                    activeTab === "xero_pnl" || activeTab === "pnl_explorer" || activeTab === "modeling_workbench" || activeTab === "forecast_summary"
                                        ? pnlActualMonths
                                        : activeTab === "bs"
                                          ? bsActualMonths
                                          : cfActualMonths,
                                )}
                                onValueChange={(v) => {
                                    const n = Number(v);
                                    if (activeTab === "xero_pnl" || activeTab === "pnl_explorer" || activeTab === "modeling_workbench" || activeTab === "forecast_summary") setPnlActualMonths(n);
                                    else if (activeTab === "bs") setBsActualMonths(n);
                                    else if (activeTab === "cashflow_explorer") setCfActualMonths(n);
                                    else setCfActualMonths(n);
                                }}
                            >
                                <SelectTrigger className="w-[140px] h-8 text-xs border-[#5BF6C8] text-[#64748b]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="3">Last 3 months</SelectItem>
                                    <SelectItem value="6">Last 6 months</SelectItem>
                                    <SelectItem value="12">Last 12 months</SelectItem>
                                    <SelectItem value="18">Last 18 months</SelectItem>
                                    <SelectItem value="24">Last 24 months</SelectItem>
                                    <SelectItem value="36">Last 36 months</SelectItem>
                                </SelectContent>
                            </Select>

                            <span className="text-xs font-medium text-muted-foreground">Forecast</span>
                            <Select
                                value={String(
                                    activeTab === "xero_pnl" || activeTab === "pnl_explorer" || activeTab === "modeling_workbench" || activeTab === "forecast_summary"
                                        ? pnlForecastMonths
                                        : activeTab === "bs"
                                          ? bsForecastMonths
                                          : cfForecastMonths,
                                )}
                                onValueChange={(v) => {
                                    const n = Number(v);
                                    if (activeTab === "xero_pnl" || activeTab === "pnl_explorer" || activeTab === "modeling_workbench" || activeTab === "forecast_summary") setPnlForecastMonths(n);
                                    else if (activeTab === "bs") setBsForecastMonths(n);
                                    else if (activeTab === "cashflow_explorer") setCfForecastMonths(n);
                                    else setCfForecastMonths(n);
                                }}
                            >
                                <SelectTrigger className="w-[150px] h-8 text-xs border-[#5BF6C8] text-[#64748b]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="3">3 months</SelectItem>
                                    <SelectItem value="6">6 months</SelectItem>
                                    <SelectItem value="12">12 months</SelectItem>
                                    <SelectItem value="18">18 months</SelectItem>
                                    <SelectItem value="24">24 months</SelectItem>
                                </SelectContent>
                            </Select>

                        </div>}

                        {(scenarios.length > 1 || SCENARIO_EDIT_TABS.has(activeTab)) && (FORECAST_TABS.has(activeTab) || activeTab === "modeling_workbench") && workspace && (
                            <div className="flex items-center gap-3 flex-wrap mt-3">
                                <span className="text-xs font-medium text-muted-foreground">Scenario</span>
                                <ScenarioSelector
                                    scenarios={scenarios}
                                    selected={activeScenario}
                                    onSelect={(s) => setSelectedScenarioId(s.id)}
                                    onCreate={workspace ? async (name) => {
                                        const newScenario = await createScenario(workspace.id, name, baselineMethod);
                                        await refresh();
                                        setSelectedScenarioId(newScenario.id);
                                    } : undefined}
                                    onDelete={workspace ? async (s) => {
                                        await deleteScenario(workspace.id, s.id);
                                        if (selectedScenarioId === s.id) setSelectedScenarioId(null);
                                        await refresh();
                                    } : undefined}
                                />
                            </div>
                        )}
                    </div>

                    {/* Tab content — scrollable area */}
                    {visibleTabs.map((tab: VNextTab) => {
                        const TabComponent = TAB_COMPONENTS[tab.key];
                        const actualMonths =
                            tab.key === "ceo_summary"
                                ? summaryActualMonths
                                : tab.key === "cashflow_explorer"
                                  ? cfActualMonths
                                  : tab.key === "bs"
                                    ? bsActualMonths
                                    : pnlActualMonths;
                        const baseForecast = DASHBOARD_SUMMARY_TABS.has(tab.key)
                            ? 0
                            : tab.key === "cashflow_explorer"
                              ? cfForecastMonths
                              : tab.key === "bs"
                                ? bsForecastMonths
                                : pnlForecastMonths;
                        // When FY is selected, ensure forecast covers remaining FY months
                        // but NOT for dashboard summary tabs (actuals-only until workbench cutover)
                        const forecastMonths = !DASHBOARD_SUMMARY_TABS.has(tab.key)
                            && FINANCIAL_TABS.has(tab.key) && fyEffectiveForecast
                            ? Math.max(baseForecast, fyEffectiveForecast)
                            : baseForecast;
                        return (
                            <TabsContent
                                key={tab.key}
                                value={tab.key}
                                data-export-tab={tab.key}
                                className="flex-1 min-h-0 min-w-0 mt-0 overflow-y-auto data-[state=active]:flex data-[state=active]:flex-col"
                            >
                                {TabComponent ? (
                                    <>
                                        {tab.key === "cashflow_explorer" ? (
                                            <>
                                                <Week13ForecastPanel
                                                    workspaceId={workspace.id}
                                                    scenarioId={activeScenario?.id ?? ""}
                                                />
                                            </>
                                        ) : (
                                            <TabComponent
                                                workspaceId={workspace.id}
                                                scenarioId={activeScenario?.id ?? ""}
                                                actualMonths={actualMonths}
                                                forecastMonths={forecastMonths}
                                                period={period}
                                                selectedMonth={selectedMonth}
                                                customFrom={customFrom}
                                                customTo={customTo}
                                                compare={compare}
                                                fyStartMonth={fyStartMonth}
                                                currentMonth={currentMonth || undefined}
                                                {...(tab.key === "ceo_summary" ? {fyEndMonth} : {})}
                                                {...(tab.key === "transactions" && txnFilters ? {initialFilters: txnFilters} : {})}
                                                {...(tab.key === "data_sync" ? {autoTriggerSyncAll} : {})}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <PlaceholderTab
                                        workspaceId={workspace.id}
                                        scenarioId={activeScenario?.id ?? ""}
                                        label={tab.label}
                                    />
                                )}
                            </TabsContent>
                        );
                    })}
                </Tabs>
            ) : (
                <Card className="mx-6">
                    <CardContent className="p-6 text-center text-muted-foreground">
                        No tabs configured for this workspace.
                    </CardContent>
                </Card>
            )}
            {isDashboardView && (
                <DashboardTour
                    open={showDashboardTour}
                    activeTab={activeTab}
                    visibleTabs={visibleTabs.map((tab) => tab.key)}
                    onClose={() => setShowDashboardTour(false)}
                    onTabChange={(tab) => setActiveTab(tab)}
                />
            )}
            {!isDashboardView && (
                <FinancialModelTour
                    open={showFinancialModelTour}
                    activeTab={activeTab}
                    visibleTabs={visibleTabs.map((tab) => tab.key)}
                    onClose={() => setShowFinancialModelTour(false)}
                    onTabChange={(tab) => setActiveTab(tab)}
                />
            )}
        </div>
    );
}
